/**
 * The Documents explorer's data hook. For the active filter/page state it loads,
 * as one coordinated fetch: the paginated document page, the tenant taxonomy (the
 * Type filter + the change-category picker share it), the location and scanner
 * registries (to resolve capture ids to names, best-effort so a documents-only
 * reader still sees the table), and the per-status facet counts for the status
 * chips. A filter change re-keys the effect, aborting any in-flight load.
 *
 * While any document is still moving through the pipeline — the `processing`
 * facet, i.e. RECEIVED or OCR_DONE — the hook re-loads itself on a timer, so a
 * document uploaded by hand is *seen* to become CLASSIFIED or NEEDS_REVIEW
 * without anyone pressing anything. Those refreshes are deliberately quiet: they
 * swap the data in place rather than dropping the screen back to skeletons,
 * because a table that blanks itself every few seconds reads as broken. The loop
 * ends by itself the moment nothing is in flight.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../../api/errors';
import type { EffectiveCategory, Location, Scanner } from '../../api/types';
import { useSession } from '../../auth/session';
import { usePolling } from '../../lib/hooks';

import { useDocumentsApi, type DocumentsPage } from './client';
import {
  serializeFilters,
  STATUS_KEYS,
  toListParams,
  type DocFilters,
  type StatusKey,
} from './filters';

/** How long after a settled load to re-check, while documents are in flight. */
export const LIVE_POLL_MS = 4000;

export interface DocumentsData {
  page: DocumentsPage;
  categories: EffectiveCategory[];
  locations: Location[];
  scanners: Scanner[];
  counts: Partial<Record<StatusKey, number>>;
}

export type DocumentsState =
  | { status: 'loading' }
  | { status: 'error'; traceId: string | null; reload: () => void }
  | {
      status: 'ready';
      data: DocumentsData;
      reload: () => void;
      /** How many documents are still mid-pipeline; > 0 means this view is polling. */
      processing: number;
    };

function traceOf(error: unknown): string | null {
  if (error instanceof ApiError && error.problem !== null) {
    const p = error.problem;
    const id = p.correlation_id ?? p.trace_id ?? p.traceId;
    if (typeof id === 'string') return id;
  }
  return null;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function useDocuments(filters: DocFilters): DocumentsState {
  const { api } = useSession();
  const docs = useDocumentsApi();
  const [state, setState] = useState<DocumentsState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  // Set only by the poll, and cleared by the load it belongs to: it decides
  // whether this run shows skeletons or swaps the data underneath the table.
  const quiet = useRef(false);

  const reload = useCallback(() => {
    quiet.current = false;
    setNonce((n) => n + 1);
  }, []);

  const refresh = useCallback(() => {
    quiet.current = true;
    setNonce((n) => n + 1);
  }, []);

  const key = serializeFilters(filters).toString();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const silent = quiet.current;
    quiet.current = false;
    if (!silent) setState({ status: 'loading' });

    // Facet counts share the dimension/search filters but not the status/page.
    const base = toListParams({ ...filters, status: 'all', page: 0 });
    const countParams = { ...base, limit: 1 };

    void (async () => {
      try {
        const [page, categoriesPage, countValues, locationsPage, scannersPage] = await Promise.all([
          docs.list(toListParams(filters), signal),
          api.categories.list(signal),
          Promise.all(
            STATUS_KEYS.map((s) =>
              docs
                .list(s === 'all' ? countParams : { ...countParams, status: s }, signal)
                .then((r) => r.total),
            ),
          ),
          // Best-effort: every tenant reader holds registries:read, but a
          // failed fetch (network hiccup, a role without it) still shouldn't
          // block the document list itself.
          api.settings.locations.list(signal).catch(() => null),
          api.settings.scanners.list(signal).catch(() => null),
        ]);
        if (signal.aborted) return;
        const counts: Partial<Record<StatusKey, number>> = {};
        STATUS_KEYS.forEach((s, i) => {
          counts[s] = countValues[i];
        });
        setState({
          status: 'ready',
          reload,
          processing: counts.processing ?? 0,
          data: {
            page,
            categories: categoriesPage.items,
            locations: locationsPage?.items ?? [],
            scanners: scannersPage?.items ?? [],
            counts,
          },
        });
      } catch (error) {
        if (signal.aborted || isAbort(error)) return;
        // A quiet refresh that fails leaves the last good table on screen: the
        // data is a few seconds stale, which beats replacing a working view with
        // an error page over one dropped poll.
        if (silent) return;
        setState({ status: 'error', traceId: traceOf(error), reload });
      }
    })();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, docs, key, nonce, reload]);

  // `state` is the restart key: a new object per settled load re-arms the timer.
  usePolling(state.status === 'ready' && state.processing > 0, LIVE_POLL_MS, state, refresh);

  return state;
}
